import { ref, type Ref, shallowRef } from "vue";
import * as Cesium from "cesium";

export function useGoogle3DTiles(
    viewer: Ref<Cesium.Viewer | null>,
    googleApiKey: string,
    google3dTilesUrl: string
) {
    const google3DTileset = shallowRef<Cesium.Cesium3DTileset | null>(null);
    const showGoogle3DTiles = ref(false);

    const loadGoogle3DTiles = async (): Promise<void> => {
        if (!viewer.value) return;

        // Check if API key is provided
        if (!googleApiKey || googleApiKey === "your_google_maps_api_key_here") {
            console.error(
                "Google Maps API key not provided. Please set VITE_GOOGLE_MAPS_API_KEY in your .env file"
            );
            console.warn("Google 3D Tiles will not be loaded without a valid API key");
            return;
        }

        try {
            // Load Google 3D Tiles
            google3DTileset.value = await Cesium.Cesium3DTileset.fromUrl(
                google3dTilesUrl,
                {
                    maximumScreenSpaceError: 24,
                    skipLevelOfDetail: true,
                    skipLevels: 1,
                    preloadWhenHidden: false,
                }
            );
            viewer.value.scene.globe.show = false;
            viewer.value.scene.primitives.add(google3DTileset.value);
            console.log("Google 3D Tiles loaded successfully");
        } catch (error) {
            console.error("Error loading Google 3D Tiles:", error);
            console.warn(
                "Failed to load Google 3D Tiles. Please check your API key and network connection."
            );
        }
    };

    const toggleGoogle3DTiles = async (): Promise<void> => {
        if (!viewer.value) return;

        showGoogle3DTiles.value = !showGoogle3DTiles.value;

        if (showGoogle3DTiles.value) {
            if (google3DTileset.value) {
                viewer.value.scene.globe.show = false;
                google3DTileset.value.show = true;
            } else {
                await loadGoogle3DTiles();
            }
        } else {
            // Hide Google 3D Tiles
            if (google3DTileset.value) {
                viewer.value.scene.globe.show = true;
                google3DTileset.value.show = false;
            }
        }
        viewer.value.scene.requestRender();
    };

    return {
        google3DTileset,
        showGoogle3DTiles,
        toggleGoogle3DTiles,
        loadGoogle3DTiles,
    };
}